import { queryExecutor } from '@/_core/cqrs';
import { Action } from '@/_core/fluxstore/types';
import { BoardActivityPanelState, BoardActivityItem } from '../types';
import { useBoardActivityStore } from '../useModule';
import { useStorymapStore } from '../../../store';
import { BoardActivityPanelQuery } from '../../api/queries';
import type { BoardActivityPanelQueryResult } from '../../api/queries';
import { mapArrayDtoPropsToCamelCase } from '@/_core/utils/mapDtos';
import { incrementQueryId, getLatestQueryId } from '../staleGuard';
import { trimActivities } from '../trimActivities';

const { registerMiddleware, registerReducer } = useBoardActivityStore();

export class LoadBoardActivities extends Action<BoardActivityPanelState> {
    public static ACTION_TYPE = 'BoardActivity::LoadActivities';
    public _res: BoardActivityPanelQueryResult | null = null;
    public _queryId = 0;

    constructor() {
        super(LoadBoardActivities.ACTION_TYPE);
    }
}

const middleware = async (state: BoardActivityPanelState, action: LoadBoardActivities): Promise<boolean> => {
    state.isLoading = true;
    state.error = null;

    const storymapStore = useStorymapStore();
    const storymapId = storymapStore.state.id;

    const queryId = incrementQueryId();
    action._queryId = queryId;

    const errorHandler = (error: any) => {
        if (queryId !== getLatestQueryId()) {
            return { isHandled: true, result: null as any };
        }
        state.isLoading = false;
        state.error = error?.message || 'Failed to load activities';
        return { isHandled: true, result: null };
    };

    try {
        const result = await queryExecutor.executeQuery<BoardActivityPanelQueryResult>(
            new BoardActivityPanelQuery(storymapId),
            errorHandler
        );

        if (result) {
            action._res = result;
        }
    } catch (e: any) {
        if (queryId !== getLatestQueryId()) {
            return false;
        }
        state.isLoading = false;
        state.error = e?.message || 'Failed to load activities';
        return false;
    }

    return true;
};

const reducer = (state: BoardActivityPanelState, action: LoadBoardActivities): void => {
    // Staleness check BEFORE mutating state: if a newer query was started after
    // this one, ignore this response entirely — including the isLoading flag,
    // which must stay true until the latest query completes.
    if (!action._res || action._queryId !== getLatestQueryId()) {
        return;
    }

    state.isLoading = false;

    const result = action._res;
    const fetchedActivities: BoardActivityItem[] = mapArrayDtoPropsToCamelCase(result.Activities);

    // Merge strategy: items already in state (from realtime) are kept, new items are inserted
    const existingIds = new Set(state.activities.map(a => a.id));
    const newActivities = fetchedActivities.filter(a => !existingIds.has(a.id));
    const merged = [...state.activities, ...newActivities];

    // Sort by datetime descending
    merged.sort((a, b) => {
        const dateA = new Date(a.datetime).getTime();
        const dateB = new Date(b.datetime).getTime();
        return dateB - dateA;
    });

    // Cap at 200 items; an operation cut by the cap is dropped as a whole (AF3)
    const knownCount = Math.min(merged.length, 200);
    state.activities = trimActivities(merged, 200);
    state.latestRelevantActivityId = state.activities[0]?.id || state.latestRelevantActivityId;

    // Reconcile totalCount: the backend snapshot may not include realtime items
    // that are already in our merged list, and the live count may already have been
    // incremented past both values. Use the largest of all three to prevent regression.
    state.totalCount = Math.max(state.totalCount, result.TotalCount, knownCount);
};

//@ts-ignore
registerMiddleware(LoadBoardActivities.ACTION_TYPE, middleware);
//@ts-ignore
registerReducer(LoadBoardActivities.ACTION_TYPE, reducer);
